import { useState, useCallback } from 'react'
import type { AppDispatch } from 'src/store/store'
import {
  addInstructorToCourse,
  createCourseInstructor,
  removeInstructorFromCourse,
  updateCourseInstructor,
  updateInstructorSequence,
} from '../../utils/api'
import { fetchAllInstructors } from '../../create.slice'
import { InstructorWithCourses } from '../types'
import type { InstructorFormValues } from '../components/modals/instructor-modal'

type UseInstructorActionsProps = {
  courseID: string | undefined
  linkedServiceID: string | undefined
  selectedEditInstructor: InstructorWithCourses | null
  closeModal: () => void
  dispatch: AppDispatch
  showSuccess: (msg: string) => void
  showError: (msg: string) => void
}

export function useInstructorActions({
  courseID,
  linkedServiceID,
  selectedEditInstructor,
  closeModal,
  dispatch,
  showSuccess,
  showError,
}: UseInstructorActionsProps) {
  let [isInstructorSaving, setIsInstructorSaving] = useState(false)
  let [pendingRemoveInstructor, setPendingRemoveInstructor] =
    useState<InstructorWithCourses | null>(null)

  let handleAddInstructor = useCallback(
    (instructorID: string) => {
      if (!courseID) return
      setIsInstructorSaving(true)
      addInstructorToCourse(instructorID, courseID)
        .then(() => {
          dispatch(fetchAllInstructors())
          showSuccess('Instructor added to course')
        })
        .catch(() => showError('Failed to add instructor'))
        .finally(() => setIsInstructorSaving(false))
    },
    [courseID, dispatch, showSuccess, showError],
  )

  let handleCreateInstructor = useCallback(
    (values: InstructorFormValues) => {
      if (!courseID || !linkedServiceID) return
      setIsInstructorSaving(true)
      createCourseInstructor(linkedServiceID, courseID, {
        name: values.name,
        credentials: values.credentials || undefined,
      })
        .then(() => {
          dispatch(fetchAllInstructors())
          showSuccess('Instructor created and added to course')
          closeModal()
        })
        .catch(() => showError('Failed to create instructor'))
        .finally(() => setIsInstructorSaving(false))
    },
    [courseID, linkedServiceID, dispatch, showSuccess, showError, closeModal],
  )

  let handleEditInstructor = useCallback(
    (values: InstructorFormValues) => {
      if (!selectedEditInstructor) return
      setIsInstructorSaving(true)
      updateCourseInstructor(selectedEditInstructor.id, {
        name: values.name,
        credentials: values.credentials || undefined,
      })
        .then(() => {
          dispatch(fetchAllInstructors())
          showSuccess('Instructor updated')
          closeModal()
        })
        .catch(() => showError('Failed to update instructor'))
        .finally(() => setIsInstructorSaving(false))
    },
    [selectedEditInstructor, dispatch, showSuccess, showError, closeModal],
  )

  let handleConfirmRemoveInstructor = useCallback(() => {
    if (!pendingRemoveInstructor || !courseID) return Promise.resolve()
    setIsInstructorSaving(true)
    return removeInstructorFromCourse(pendingRemoveInstructor.id, courseID)
      .then(() => {
        dispatch(fetchAllInstructors())
        showSuccess('Instructor removed from course')
        setPendingRemoveInstructor(null)
      })
      .catch(() => showError('Failed to remove instructor'))
      .finally(() => setIsInstructorSaving(false))
  }, [pendingRemoveInstructor, courseID, dispatch, showSuccess, showError])

  let handleReorderInstructors = useCallback(
    (reordered: InstructorWithCourses[]) => {
      if (!courseID) return
      setIsInstructorSaving(true)
      updateInstructorSequence(
        courseID,
        reordered.map((i, idx) => ({ instructorID: i.id, sequence: idx })),
      )
        .then(() => dispatch(fetchAllInstructors()))
        .catch(() => showError('Failed to update instructor order'))
        .finally(() => setIsInstructorSaving(false))
    },
    [courseID, dispatch, showError],
  )

  return {
    isInstructorSaving,
    pendingRemoveInstructor,
    setPendingRemoveInstructor,
    handleAddInstructor,
    handleCreateInstructor,
    handleEditInstructor,
    handleConfirmRemoveInstructor,
    handleReorderInstructors,
  }
}
